import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { v4 as uuid } from 'uuid';
import classNames from 'classnames';
import { kebabCase } from 'lodash';
import { Input, InputGroupAddon, InputGroupText, InputGroup } from 'reactstrap';
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { enabledSelector, labelSelector, unitsSelector, valueSelector, conformanceSelector, newPathSelector } from '../selectors/index';
import entityComposition from '../../common/classes/entityComposition';
import Label from './label';
import ConformanceTooltip from './conformanceTooltip';
import logValueFunc from '../commonActions/logValue';
import onChangeFunc from '../commonActions/onChange';
import localChangeSelector from '../selectors/localChangeSelector';
const setMinIntegerDigits = (value, minimumIntegerDigits) => {
  if (minimumIntegerDigits === 0) {
    return value;
  }
  const isNegative = value.toString().substring(0, 1) === '-';
  const absoluteValue = isNegative ? value.toString().substring(1) : value;
  // We only want to deal with the integers
  const parts = absoluteValue.toString().split('.');
  const integers = parts[0].padStart(minimumIntegerDigits, 0);
  // See if there are decimal units that need to be appended
  const repackaged = parts.length === 1 ? integers : [integers, parts[1]].join('.');
  // Reattach the negative sign
  return isNegative ? "-".concat(repackaged) : repackaged;
};
const setMaxIntegerDigits = (value, maximumIntegerDigits) => {
  if (maximumIntegerDigits === 0) {
    return value;
  }
  const stringValue = value.toString();
  const isNegative = stringValue.substring(0, 1) === '-';
  const absoluteValue = isNegative ? stringValue.substring(1) : stringValue;
  // We only want to deal with the integers
  const parts = absoluteValue.split('.');
  let integers = parts[0];
  if (integers.length > maximumIntegerDigits) {
    integers = integers.substring(0, maximumIntegerDigits);
  }
  // See if there are decimal units that need to be appended
  const repackaged = parts.length === 1 ? integers : [integers, parts[1]].join('.');
  // Reattach the negative sign
  return isNegative ? "-".concat(repackaged) : repackaged;
};
const setFractionDigits = (value, maximumFractionDigits, minimumFractionDigits) => {
  // Handle Maximum by rounding
  const exp = 10 ** maximumFractionDigits;
  const roundedValue = Math.round(value * exp) / exp;
  // Handle Minimum by appending 0's if need b
  const numberOfFractionDigits = roundedValue.toString().split('.')[1];
  if (numberOfFractionDigits >= minimumFractionDigits) {
    return roundedValue.toString();
  }
  return roundedValue.toFixed(minimumFractionDigits);
};
const getDisplayValue = (value, maximumFractionDigits, minimumFractionDigits, minimumIntegerDigits, maximumIntegerDigits) => {
  if (value === null || value === '') {
    return null;
  }
  const fraction = setFractionDigits(value, maximumFractionDigits, minimumFractionDigits);
  let finalValue = setMinIntegerDigits(fraction, minimumIntegerDigits);
  finalValue = setMaxIntegerDigits(finalValue, maximumIntegerDigits);
  return finalValue;
};
const NumberField = _ref => {
  let _ref$conformanceText = _ref.conformanceText,
    conformanceText = _ref$conformanceText === void 0 ? null : _ref$conformanceText,
    _ref$displayUnit = _ref.displayUnit,
    displayUnit = _ref$displayUnit === void 0 ? '' : _ref$displayUnit,
    enabled = _ref.enabled,
    _ref$internalLabel = _ref.internalLabel,
    internalLabel = _ref$internalLabel === void 0 ? null : _ref$internalLabel,
    _ref$label = _ref.label,
    label = _ref$label === void 0 ? null : _ref$label,
    _ref$maximumFractionD = _ref.maximumFractionDigits,
    maximumFractionDigits = _ref$maximumFractionD === void 0 ? 2 : _ref$maximumFractionD,
    _ref$minimumFractionD = _ref.minimumFractionDigits,
    minimumFractionDigits = _ref$minimumFractionD === void 0 ? 0 : _ref$minimumFractionD,
    _ref$minimumIntegerDi = _ref.minimumIntegerDigits,
    minimumIntegerDigits = _ref$minimumIntegerDi === void 0 ? 0 : _ref$minimumIntegerDi,
    _ref$maximumIntegerDi = _ref.maximumIntegerDigits,
    maximumIntegerDigits = _ref$maximumIntegerDi === void 0 ? 0 : _ref$maximumIntegerDi,
    onChange = _ref.onChange,
    _ref$placeholder = _ref.placeholder,
    placeholder = _ref$placeholder === void 0 ? '' : _ref$placeholder,
    _ref$testingIdentifie = _ref.testingIdentifier,
    testingIdentifier = _ref$testingIdentifie === void 0 ? 'default-id' : _ref$testingIdentifie,
    _ref$value = _ref.value,
    propsValue = _ref$value === void 0 ? null : _ref$value,
    path = _ref.path,
    logValue = _ref.logValue,
    operationComplete = _ref.operationComplete,
    operationPending = _ref.operationPending,
    localChangeFunc = _ref.localChangeFunc;
  // a direct reference to the input field.
  const valueRef = useRef();
  // this is used to validate the input.
  const _useState = useState("".concat(propsValue)),
    _useState2 = _slicedToArray(_useState, 2),
    lastValue = _useState2[0],
    setLastValue = _useState2[1];
  const showConformance = !!conformanceText;
  const inputAddon = internalLabel || displayUnit || '';
  const displayValue = getDisplayValue(propsValue, maximumFractionDigits, minimumFractionDigits, minimumIntegerDigits, maximumIntegerDigits);
  const handleBlur = async () => {
    let value = valueRef.current.value;
    if (value === '' || value === null) {
      // Allow Clearing input
      value = null;
    } else {
      // Validate input, or reject by returning previous
      value = value.replace(/[^\d.-]/g, '');
    }
    const updatedValue = getDisplayValue(value, maximumFractionDigits, minimumFractionDigits, minimumIntegerDigits, maximumIntegerDigits);
    let saveValue = null;
    if (updatedValue !== null) {
      saveValue = !Number.isNaN(Number(updatedValue)) ? Number.parseFloat(updatedValue) : 0;
    }
    if (saveValue !== propsValue) {
      var _valueRef$current;
      // let redux know we have a complex operation
      // afoot.
      await operationPending();
      const change = localChangeFunc({
        path,
        value: saveValue
      });
      await onChange(change);
      const log = {
        value: saveValue,
        displayValue: "".concat(updatedValue, " ").concat(displayUnit || '')
      };
      // log the display value.
      await logValue(log);
      // done with complex operation.
      if ((valueRef === null || valueRef === void 0 || (_valueRef$current = valueRef.current) === null || _valueRef$current === void 0 ? void 0 : _valueRef$current.value) !== updatedValue) {
        // we updated props, we should update value here.
        if (updatedValue === null) {
          valueRef.current.value = '';
        }
        if (valueRef.current !== undefined) {
          valueRef.current.value = updatedValue;
        }
      }
      return operationComplete();
    }
    return undefined;
  };
  const handleChange = async e => {
    const inputValue = e.target.value;
    let updatedValue = '';
    if (inputValue === '' || inputValue === null) {
      // Allow Clearing input
      updatedValue = null;
    } else {
      // Validate input, or reject by returning previous
      updatedValue = /^-?\d*[.]?\d*$/.exec(inputValue.toString()) ? inputValue : lastValue;
      if (updatedValue === 'null') {
        updatedValue = null;
      }
    }
    if (updatedValue !== inputValue) {
      if (updatedValue === null) {
        valueRef.current.value = '';
      } else {
        valueRef.current.value = updatedValue;
      }
      setLastValue(updatedValue);
    }
  };

  // sometimes value can change outside of
  // the input - via behaviors for example.
  useEffect(() => {
    var _valueRef$current2;
    // eslint-disable-next-line no-undef
    if ((valueRef === null || valueRef === void 0 || (_valueRef$current2 = valueRef.current) === null || _valueRef$current2 === void 0 ? void 0 : _valueRef$current2.value) !== displayValue) {
      // we updated props, we should update value here.
      if (valueRef.current !== undefined) {
        valueRef.current.value = displayValue;
      }
    }
  }, [displayValue]);
  const className = 't-ft';
  const id = "input-".concat(kebabCase(label), "-").concat(uuid());
  const testID = "".concat(className, "-").concat(kebabCase(testingIdentifier));
  const formClasses = classNames({
    ["".concat(className, "-").concat(id)]: true,
    ["".concat(testID, "-input")]: true,
    disabled: !enabled,
    'tqc-control': true,
    'tqc-number-field': true
  });
  return /*#__PURE__*/React.createElement("div", {
    className: "".concat(testID, "-form")
  }, label && /*#__PURE__*/React.createElement(Label, {
    value: label,
    style: {
      height: '32px'
    }
  }), /*#__PURE__*/React.createElement(InputGroup, null, /*#__PURE__*/React.createElement(Input, {
    id: id,
    "data-testid": testID,
    type: "text",
    placeholder: placeholder,
    onChange: handleChange,
    onBlur: handleBlur,
    className: formClasses,
    defaultValue: displayValue || '',
    innerRef: valueRef,
    disabled: !enabled,
    invalid: showConformance
  }), inputAddon ? /*#__PURE__*/React.createElement(InputGroupAddon, {
    addonType: "append"
  }, /*#__PURE__*/React.createElement(InputGroupText, null, inputAddon)) : null), /*#__PURE__*/React.createElement(ConformanceTooltip, {
    errorText: conformanceText,
    target: id
  }));
};
NumberField.propTypes = {
  path: PropTypes.string.isRequired,
  conformanceText: PropTypes.string,
  enabled: PropTypes.bool.isRequired,
  displayUnit: PropTypes.string,
  internalLabel: PropTypes.string,
  label: PropTypes.string,
  maximumFractionDigits: PropTypes.number,
  // used by getDisplayValue
  minimumIntegerDigits: PropTypes.number,
  maximumIntegerDigits: PropTypes.number,
  // used by getDisplayValue
  minimumFractionDigits: PropTypes.number,
  // used by getDisplayValue
  onChange: PropTypes.func.isRequired,
  logValue: PropTypes.func.isRequired,
  operationPending: PropTypes.func.isRequired,
  operationComplete: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  testingIdentifier: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  localChangeFunc: PropTypes.func.isRequired
};
export { NumberField as UncomposedNumberField };
export default entityComposition(NumberField, [], {
  conformanceText: conformanceSelector,
  displayUnit: unitsSelector,
  enabled: enabledSelector,
  label: labelSelector,
  value: valueSelector,
  path: newPathSelector,
  logValue: logValueFunc,
  onChange: onChangeFunc,
  localChangeFunc: localChangeSelector
});